<template>
  <div class="message-center-container">
    <el-card class="message-card" shadow="never">
      <template #header>
        <div class="card-header">
          <h2 class="page-title">消息中心</h2>
        </div>
      </template>

      <!-- 顶部说明 -->
      <div class="summary-bar">
        <div class="summary-tip">按业务类型汇总展示，点击卡片查看该分类下的所有消息。</div>
      </div>

      <!-- 分类卡片列表（参考小程序） -->
      <div class="category-list-container">
        <el-scrollbar height="650px">
          <div v-if="loading && categories.length === 0" class="loading-wrapper">
            <el-icon class="is-loading" :size="32"><Loading /></el-icon>
            <p>加载中...</p>
          </div>

          <div v-else-if="categories.length === 0" class="empty-wrapper">
            <el-empty description="暂无消息" />
          </div>

          <div v-else class="category-list">
            <div
              v-for="item in categories"
              :key="item.categoryKey"
              class="category-card"
              @click="openCategory(item)"
            >
              <div class="category-header">
                <div class="category-title">
                  <span class="category-title-text">{{ item.categoryTitle }}</span>
                  <el-badge
                    v-if="(Number(item.unreadCount) || 0) > 0"
                    :value="item.unreadCount"
                    class="category-unread"
                  />
                </div>
                <span class="category-time">{{ formatTime(item.latestTime) }}</span>
              </div>
              <div class="category-content">{{ item.latestContent }}</div>
              <div class="category-footer">
                <span class="category-count">共 {{ item.totalCount }} 条</span>
                <span class="category-action">查看详情</span>
              </div>
            </div>
          </div>
        </el-scrollbar>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { getMessageCategorySummary } from '../api/message'
const router = useRouter()

const categories = ref([])
const loading = ref(false)

const loadCategorySummary = async () => {
  if (loading.value) return
  loading.value = true
  try {
    const res = await getMessageCategorySummary()
    if (res.code === '000000' && Array.isArray(res.data)) {
      categories.value = res.data
    } else {
      throw new Error(res.msg || '加载失败')
    }
  } catch (e) {
    console.error('加载消息分类汇总失败:', e)
    ElMessage.error(e.message || '加载失败')
    categories.value = []
  } finally {
    loading.value = false
  }
}

// 格式化时间
const formatTime = (timeStr) => {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  if (isNaN(date.getTime())) return ''

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  const diff = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24))

  if (diff === 0) {
    // 今天：显示时间
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `今天 ${hours}:${minutes}`
  } else if (diff === 1) {
    // 昨天
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `昨天 ${hours}:${minutes}`
  } else if (diff < 7) {
    // 一周内：显示星期
    const weekdays = ['日', '一', '二', '三', '四', '五', '六']
    return `星期${weekdays[date.getDay()]}`
  } else {
    // 更早：显示日期
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${month}月${day}日`
  }
}

// 返回到消息中心时重新拉取分类汇总，保证未读数量和角标最新
onActivated(() => {
  loadCategorySummary()
})

const openCategory = (item) => {
  if (!item?.categoryKey) return
  router.push({
    path: '/home/message-center/detail',
    query: {
      categoryKey: item.categoryKey,
      title: item.categoryTitle
    }
  })
}

onMounted(() => {
  loadCategorySummary()
})
</script>

<style scoped>
.message-center-container {
  padding: 20px;
  height: 100%;
}

.message-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.message-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

/* 顶部操作栏 */
.summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e7ed;
}

.summary-tip {
  color: #909399;
  font-size: 13px;
}

/* 分类卡片容器 */
.category-list-container {
  flex: 1;
  overflow: hidden;
  background-color: #f5f7fb;
  border-radius: 10px;
  padding: 12px;
}

.category-list {
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  grid-auto-rows: 1fr;
  gap: 18px;
}

.category-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px 18px 14px;
  border: 1px solid #e9edf5;
  cursor: pointer;
  transition: all 0.25s ease;
}

.category-card:hover {
  box-shadow: 0 8px 22px rgba(25, 33, 61, 0.08);
  transform: translateY(-1px);
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.category-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.category-title-text {
  font-size: 18px;
  font-weight: 700;
  color: #2c2f36;
}

.category-unread :deep(.el-badge__content) {
  border: none;
  box-shadow: 0 6px 14px rgba(245, 108, 108, 0.25);
}

.category-time {
  font-size: 13px;
  color: #909399;
  white-space: nowrap;
}

.category-content {
  font-size: 14px;
  color: #606266;
  line-height: 1.7;
  margin-bottom: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.category-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px dashed #e7eaf2;
}

.category-count {
  font-size: 13px;
  color: #909399;
}

.category-action {
  font-size: 14px;
  color: #4c6fff;
  font-weight: 600;
}

/* 加载状态 */
.loading-wrapper,
.empty-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;
}

.loading-wrapper .el-icon {
  margin-bottom: 12px;
}

.load-more,
.no-more {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}
</style>

